import { apiFetch } from '$lib/api/helpers';
import { API_BASE, getApiAuthHeaders } from '$lib/constants/api';
import { clearAccessToken, refreshSession } from '$lib/auth';
import type {
	ConfirmUploadRequest,
	ConfirmUploadResponse,
	DownloadURLResponse,
	ProjectFile,
	UploadURLRequest,
	UploadURLResponse
} from '$lib/types/project_file';

const PATH = '/project_files/';

export async function getUploadUrl(request: UploadURLRequest) {
	return apiFetch<UploadURLResponse>(`${PATH}get_upload_url/`, {
		method: 'POST',
		body: JSON.stringify(request)
	});
}

export async function uploadToS3(file: File, url: string) {
	const response = await fetch(url, {
		method: 'PUT',
		headers: {
			'Content-Type': file.type
		},
		body: file
	});
	if (!response.ok) {
		throw new Error(`Unable to upload file to s3: ${response.statusText}`);
	}
}

export async function confirmUpload(request: ConfirmUploadRequest) {
	return apiFetch<ConfirmUploadResponse>(`${PATH}confirm_upload/`, {
		method: 'POST',
		body: JSON.stringify(request)
	});
}

export async function fetchProjectFiles(projectId: number, unitId?: number, roomId?: number) {
	const params = new URLSearchParams({ project: String(projectId) });
	if (unitId != null) params.set('unit', String(unitId));
	if (roomId != null) params.set('room', String(roomId));
	return apiFetch<unknown[]>(`${PATH}?${params.toString()}`);
}

export async function deleteFile(id: number) {
	return apiFetch<void>(`${PATH}${id}/`, { method: 'DELETE' });
}

export async function getDownloadURL(id: number): Promise<DownloadURLResponse> {
	return apiFetch<DownloadURLResponse>(`${PATH}${id}/download_url/`);
}

// moveFile assigns (or clears, with folderId=null) the folder a file lives in.
export async function moveFile(id: number, folderId: number | null) {
	return apiFetch<ProjectFile>(`${PATH}${id}/`, {
		method: 'PATCH',
		body: JSON.stringify({ folder_id: folderId })
	});
}

// fetchFolderZip streams a folder's files as a zip Blob. Mirrors
// fetchBillOfMaterials/fetchFirmwareMappings: manual fetch (not apiFetch)
// because the response is a binary blob, not JSON.
export async function fetchFolderZip(projectId: number, folderId: number, unitId?: number) {
	const url = new URL(`${API_BASE}${PATH}download_zip/`);
	url.searchParams.set('project_id', String(projectId));
	url.searchParams.set('folder_id', String(folderId));
	if (unitId != null) url.searchParams.set('unit_id', String(unitId));

	let retried = false;
	while (true) {
		const response = await fetch(url, {
			method: 'GET',
			headers: getApiAuthHeaders()
		});

		if (response.status === 401 && !retried) {
			try {
				await refreshSession();
				retried = true;
				continue;
			} catch {
				clearAccessToken();
				throw new Error('Authentication required. Please log in again.');
			}
		}

		if (!response.ok) {
			throw new Error(`Unable to download folder: ${response.statusText}`);
		}
		return response.blob();
	}
}

// Assign or clear room metadata without changing the file's folder.
export async function assignFileRoom(id: number, roomId: number | null) {
	return apiFetch<ProjectFile>(`${PATH}${id}/`, {
		method: 'PATCH',
		body: JSON.stringify({ room: roomId })
	});
}
